import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { getTheme, ITextField, ITextFieldProps, mergeStyleSets, TextField } from '@fluentui/react'

export enum StatusIcon {
    None = 'NONE',
    Success = 'SUCCESS',
    Failed = 'FAILED',
    Editing = 'EDITING',
    Saving = 'SAVING',
}

export type EditableTextFieldProps = ITextFieldProps & {
    selectionStart?: number
    selectionEnd?: number
    statusIcon?: StatusIcon
    save?: (value: string) => Promise<StatusIcon | undefined>
    onFormat?: (value: string) => string
}

const statusIcons = new Map<StatusIcon, { name: string; color: string }>([
    [StatusIcon.Success, { name: 'Completed', color: getTheme().palette.green }],
    [StatusIcon.Failed, { name: 'Error', color: getTheme().palette.red }],
    [StatusIcon.Editing, { name: 'Processing', color: getTheme().palette.neutralPrimary }],
    [StatusIcon.Saving, { name: 'CloudUpload', color: getTheme().palette.neutralPrimary }],
])

export default function EditableTextField(props: EditableTextFieldProps): ReactElement {
    const ref = useRef<ITextField>()
    const [value, setValue] = useState<string>('')
    const [icon, setIcon] = useState<StatusIcon>(props.statusIcon || (props.save ? StatusIcon.Success : StatusIcon.None))
    const styles = useMemo<ITextFieldProps['styles']>(
        () => mergeStyleSets(props.styles, props.save && icon !== StatusIcon.None && { icon: { color: statusIcons.get(icon).color } }),
        [props.styles, icon],
    )

    useEffect(() => {
        if (props.statusIcon) {
            setIcon(props.statusIcon)
        }
    }, [props.value, props.statusIcon])

    useEffect(() => {
        if (ref.current) {
            if (props.selectionStart || props.selectionEnd) {
                ref.current.select()
            }
            if (props.selectionStart) {
                ref.current.setSelectionStart(props.selectionStart)
            }
            if (props.selectionEnd) {
                ref.current.setSelectionEnd(props.selectionEnd)
            }
        }
    }, [ref.current])

    useEffect(() => {
        if ([StatusIcon.None, StatusIcon.Failed, StatusIcon.Success].includes(icon)) {
            setValue(props.value || '')
        }
    }, [props.value, icon])

    return (
        <TextField
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) =>
                setValue(props.onFormat?.(newValue) ?? newValue ?? '')
            }
            {...props}
            {...(props.save && icon !== StatusIcon.None && { iconProps: { iconName: statusIcons.get(icon).name } })}
            componentRef={ref}
            value={value}
            styles={styles}
            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (props.save) {
                    setIcon(StatusIcon.Editing)
                }
                props.onFocus?.(event)
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (props.save) {
                    setIcon(StatusIcon.Saving)
                    props.save(value).then(
                        (statusIcon: StatusIcon) => setIcon(statusIcon || StatusIcon.Success),
                        () => setIcon(StatusIcon.Failed),
                    )
                }
                props.onBlur?.(event)
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (props.save) {
                    switch (event.key) {
                        case 'Escape':
                            setValue(props.value)
                            ref.current?.blur()
                            break
                        case 'Enter':
                            ref.current?.blur()
                            break
                    }
                }
                props.onKeyDown?.(event)
            }}
        />
    )
}
